export type ActivityItem = {
  event_type?: string;
  direction?: string;
  occurred_at?: string;
  alias_email?: string;
  delivered_to_email?: string;
  replied_to_email?: string;
  sender_name?: string;
  sender_email?: string;
  meta?: {
    smtp_id?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

export type ProcessedActivity = ActivityItem & {
  _displayLabel: string;
  _hasCheck: boolean;
  occurred_at: string;
};

type ActivityLabels = {
  received: string;
  replied: string;
};

const displayLabelFor = (activity: ActivityItem, labels: ActivityLabels) => {
  if (activity.direction === 'inbound') return labels.received;
  if (activity.direction === 'outbound') return labels.replied;
  return activity.event_type || '';
};

const firstValue = <T>(items: ActivityItem[], getter: (item: ActivityItem) => T | undefined) => {
  for (const item of items) {
    const value = getter(item);
    if (value) return value;
  }
  return undefined;
};

export const formatActivitySender = (activity: Pick<ActivityItem, 'sender_name' | 'sender_email'>) => {
  if (activity.sender_name && activity.sender_email) {
    return `${activity.sender_name} <${activity.sender_email}>`;
  }
  return activity.sender_email || activity.sender_name || '-';
};

export const formatActivityDetails = (activity: Pick<ActivityItem, 'meta'>) => {
  return activity.meta ? JSON.stringify(activity.meta) : '-';
};

export const processActivities = (activities: ActivityItem[], labels: ActivityLabels, limit?: number) => {
  const groups: { [key: string]: ActivityItem[] } = {};
  const standalone: ProcessedActivity[] = [];

  activities.forEach((activity) => {
    const smtpId = activity.meta?.smtp_id;
    if (smtpId) {
      if (!groups[smtpId]) groups[smtpId] = [];
      groups[smtpId].push(activity);
      return;
    }

    standalone.push({
      ...activity,
      occurred_at: activity.occurred_at || '',
      _displayLabel: displayLabelFor(activity, labels),
      _hasCheck: activity.event_type === 'forwarded_success',
    });
  });

  const grouped = Object.values(groups).map((group) => {
    const hasForwarded = group.some((item) => item.event_type === 'forwarded_success');
    const inbound = group.find((item) => item.direction === 'inbound');
    const outbound = group.find((item) => item.direction === 'outbound');
    const base = inbound || outbound || group[0];

    return {
      ...base,
      occurred_at: base.occurred_at || '',
      _displayLabel: displayLabelFor(base, labels),
      _hasCheck: hasForwarded,
      delivered_to_email: firstValue(group, (item) => item.delivered_to_email),
      replied_to_email: firstValue(group, (item) => item.replied_to_email),
      sender_name: firstValue(group, (item) => item.sender_name),
      sender_email: firstValue(group, (item) => item.sender_email),
    };
  });

  const processed = [...standalone, ...grouped].sort((a, b) => (
    new Date(b.occurred_at || 0).getTime() - new Date(a.occurred_at || 0).getTime()
  ));

  return typeof limit === 'number' ? processed.slice(0, limit) : processed;
};
